import { TemplateValidationError } from 'src/models/dto/TemplateValidationError'

export enum ImportStep {
    Upload = 0,
    Review = 1,
    Create = 2,
}

export const IMPORT_STEP_LABELS: Record<ImportStep, string> = {
    [ImportStep.Upload]: 'Upload',
    [ImportStep.Review]: 'Review',
    [ImportStep.Create]: 'Create',
}

export const ROWS_PER_PAGE = 10

const MAX_CELL_CHARS = 40

export const truncateCell = (value?: string): string =>
    value && value.length > MAX_CELL_CHARS ? `${value.slice(0, MAX_CELL_CHARS)}…` : value ?? ''

export const ROW_BORDER = { borderBottom: '1px solid #E1E4EA' }

export const pluralize = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`

export interface ParsedSpreadsheet {
    headers: string[]
    rowCount: number
    containerCount: number
    rows: Record<string, string>[]
}

export interface SpreadsheetValidation {
    missing: string[]
    extra: string[]
}

export interface RowValidation {
    byRow: Record<number, string[]>
    general: string[]
}

const SPREADSHEET_LINE = /^Spreadsheet line (\d+):\s*(.*)$/

const dedupe = (messages: string[]): string[] =>
    messages.filter((message, index) => messages.indexOf(message) === index)

export const FIRST_DATA_LINE = 2

export const mapValidationErrorsToRows = (
    errors: TemplateValidationError[],
    rowCount: number
): RowValidation => {
    const byRow: Record<number, string[]> = {}
    const general: string[] = []

    errors.forEach(({ message }) => {
        const match = SPREADSHEET_LINE.exec(message)
        const rowIndex = match ? Number(match[1]) - FIRST_DATA_LINE : -1
        if (match && rowIndex >= 0 && rowIndex < rowCount) {
            byRow[rowIndex] = dedupe([...(byRow[rowIndex] ?? []), match[2]])
        } else if (message) {
            general.push(message)
        }
    })

    return { byRow, general }
}

export const DRY_RUN_CONCURRENCY = 4

export const dryRunRows = async (
    rows: Record<string, string>[],
    validateRow: (row: Record<string, string>) => Promise<string[]>,
    onRowDone: (completed: number) => void,
    concurrency: number = DRY_RUN_CONCURRENCY
): Promise<RowValidation> => {
    const byRow: Record<number, string[]> = {}
    let nextIndex = 0
    let completed = 0

    const worker = async () => {
        while (nextIndex < rows.length) {
            const index = nextIndex++
            try {
                const messages = dedupe(await validateRow(rows[index]))
                if (messages.length > 0) {
                    byRow[index] = messages
                }
            } catch (e) {
                byRow[index] = [e instanceof Error ? e.message : 'This row could not be validated.']
            }
            onRowDone(++completed)
        }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, rows.length) }, () => worker()))

    return { byRow, general: [] }
}

export const validateSpreadsheet = (
    spreadsheet: ParsedSpreadsheet,
    placeholders: string[]
): SpreadsheetValidation => {
    const headerSet = new Set(spreadsheet.headers)
    const placeholderSet = new Set(placeholders)

    return {
        missing: placeholders.filter((name) => !headerSet.has(name)),
        extra: spreadsheet.headers.filter((header) => !placeholderSet.has(header)),
    }
}
